import "@app/App.css";
import { Button } from "@comps/ui/button";
import { Form } from "@comps/ui/form";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { BASE_URL } from "@utils/constants";
import { downloadBase64File } from "@utils/files";
import { toastError } from "@utils/toasts";
import { TW_Button, TW_WorkspaceMenuItem, getWsButtonStyle, useHover } from "@utils/twClasses";
import { get } from "@utils/utils";
import { t } from "i18next";
import { Download } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { z } from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Workspace } from "../../dto/workspaces";
import { useModelStore } from "src/store";

interface WorkspaceMenuExportItemProps {
  workspace: Workspace;
  onWorkpaceExport: (workspace: Workspace) => void;
  closeMenu: () => void;
}

function WorkspaceMenuExportItem({ workspace, onWorkpaceExport, closeMenu }: WorkspaceMenuExportItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const workspaceExported = (base64: string) => {
    downloadBase64File(base64, `${workspace.name}.cpz`);
    setIsDialogOpen(false);
    closeMenu();
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(false);
    closeMenu();
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsDialogOpen(true);
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger className={TW_WorkspaceMenuItem} onClick={handleClick}><Download className="WorkspaceMenuItemIcon" />{t("workspace.button.export.text")}</DialogTrigger>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t("workspace.dialog.export.title")}</DialogTitle>
          <DialogDescription>{t("workspace.dialog.export.description")}</DialogDescription>
        </DialogHeader>
        <WorkspaceExportForm initialWorkspace={workspace} onWorkpaceExport={workspaceExported} onCancel={cancel} />
      </DialogContent>
    </Dialog>
  )
}

interface WorkspaceExportFormProps {
  initialWorkspace: Workspace;
  onWorkpaceExport: (base64: string) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function WorkspaceExportForm({ initialWorkspace, onWorkpaceExport, onCancel }: WorkspaceExportFormProps) {
  const formSchema = z.object({})
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelButtonStyle = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okButtonStyle = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const form = useForm<z.infer<typeof formSchema>>({
  })

  function onSubmit(values: z.infer<typeof formSchema>) {

    get(`${BASE_URL}/model/workspaces/${initialWorkspace._key}/export`).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            toastError(translate(t, cError.i18n, cError.params));
          } else {
            toastError(cError.message);
          }
        } else {
          const workspaceForExport = response as string;
          onWorkpaceExport(workspaceForExport);
        }
      }
    })
  }

  function handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    form.handleSubmit(onSubmit)();
  }

  return (
    <Form {...form}>
      <form className="space-y-8">
        <div className="ButtonList">
          <Button className={TW_Button} {...cancelButtonStyle} onClick={onCancel}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_Button} {...okButtonStyle} onClick={handleSubmit}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default WorkspaceMenuExportItem;
